import React, { useEffect, useState } from 'react';
import { loadLoginScreenStyles } from '@utils/loadWebStyles';
import {
  GLASS_SPLIT_FADE_MS,
  GLASS_SPLIT_HOLD_MS,
  GLASS_SPLIT_MIN_VISIBLE_MS,
  GLASS_SPLIT_MORPH_MS,
  GLASS_SPLIT_POST_READY_MS,
} from './loginGlassSplitConfig';
import {
  getGlassSplitPhase,
  getGlassSplitSnapshot,
  hideLoginGlassSplit,
  isGlassSplitDashboardReady,
  subscribeGlassSplitPhase,
  type GlassRect,
  type GlassSplitPhase,
  type GlassSplitSnapshot,
} from './loginGlassSplitTransition';

type PanelKey = 'menu' | 'messages' | 'footer';

function rectStyle(rect: GlassRect, themeLight: boolean): React.CSSProperties {
  return {
    position: 'fixed',
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
    borderRadius: rect.borderRadius,
    border: themeLight
      ? '1px solid rgba(229, 231, 235, 0.95)'
      : '1px solid rgba(55, 65, 81, 0.95)',
    backgroundColor: themeLight
      ? 'rgba(255, 255, 255, 0.72)'
      : 'rgba(0, 0, 0, 0.58)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    boxShadow: themeLight
      ? '0 8px 32px rgba(15, 23, 42, 0.10), inset 0 1px 0 rgba(255,255,255,0.5)'
      : '0 8px 32px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255,255,255,0.08)',
    boxSizing: 'border-box',
    pointerEvents: 'none',
    willChange: 'top, left, width, height, border-radius, opacity',
    transitionProperty: 'top, left, width, height, border-radius, opacity',
    transitionDuration: `${GLASS_SPLIT_MORPH_MS}ms`,
    transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
  };
}

function msUntilMinVisible(startedAt: number): number {
  return Math.max(0, GLASS_SPLIT_MIN_VISIBLE_MS - (Date.now() - startedAt));
}

/**
 * Web/Electron overlay: morphs the login glass card into menu / messages / footer shells
 * while Dashboard mounts under the LoginArt curtain.
 * Holds until Dashboard signals ready (covers lazy DashStack), then fades.
 */
export default function GlassSplitOverlay() {
  const [phase, setPhase] = useState<GlassSplitPhase>(getGlassSplitPhase);
  const [snap, setSnap] = useState<GlassSplitSnapshot | null>(getGlassSplitSnapshot);
  const [morphed, setMorphed] = useState(false);
  const [fading, setFading] = useState(false);

  useEffect(() => subscribeGlassSplitPhase(setPhase), []);

  useEffect(() => {
    if (phase === 'hidden') {
      setMorphed(false);
      setFading(false);
      setSnap(null);
      return;
    }

    const next = getGlassSplitSnapshot();
    setSnap(next);
    if (!next) {
      hideLoginGlassSplit();
      return;
    }

    loadLoginScreenStyles();
    document.documentElement.classList.add('login-glass-splitting');

    const morphTimer = window.setTimeout(() => setMorphed(true), GLASS_SPLIT_HOLD_MS);

    return () => {
      window.clearTimeout(morphTimer);
    };
  }, [phase === 'hidden' ? 'hidden' : 'active']);

  // Fade only after Dashboard ready (or max timeout → phase 'fading'), and after min settle.
  useEffect(() => {
    if (phase !== 'fading') return;

    const startedAt = getGlassSplitSnapshot()?.startedAt ?? Date.now();
    const wait =
      msUntilMinVisible(startedAt) +
      (isGlassSplitDashboardReady() ? GLASS_SPLIT_POST_READY_MS : 0);

    const fadeTimer = window.setTimeout(() => setFading(true), wait);
    const hideTimer = window.setTimeout(
      () => hideLoginGlassSplit(),
      wait + GLASS_SPLIT_FADE_MS,
    );

    return () => {
      window.clearTimeout(fadeTimer);
      window.clearTimeout(hideTimer);
    };
  }, [phase]);

  if ((phase !== 'splitting' && phase !== 'fading') || !snap) return null;

  const panels: PanelKey[] = ['menu', 'messages', 'footer'];
  const source = snap.source;
  const themeLight = snap.themeLight;

  return (
    <div
      className={`login-glass-split ${fading ? 'login-glass-split--fading' : ''}`.trim()}
      aria-hidden
    >
      {panels.map((key) => {
        const target = snap.targets[key];
        const rect = morphed ? target : source;
        return (
          <div
            key={key}
            className={`login-glass-split__panel login-glass-split__panel--${key}`}
            style={rectStyle(rect, themeLight)}
          />
        );
      })}
    </div>
  );
}
